import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip } from 'recharts';
import { biGet, errText, C, tooltipStyle } from './biApi';
import { Loading, ErrorBox } from './BiUi';

/** Individual Student 360 profile: attendance consistency + homework discipline + exam percentile. */
export default function Student360Modal({ user, classId, studentId, year, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    setData(null); setErr('');
    biGet(user, `/api/analytics/student/${encodeURIComponent(classId)}/${encodeURIComponent(studentId)}`, { year })
      .then((d) => { if (!live) return; d.error ? setErr(d.error) : setData(d); })
      .catch((e) => live && setErr(errText(e)));
    return () => { live = false; };
  }, [user, classId, studentId, year]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-800 bg-slate-950/50 p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-2xl font-black tracking-tight text-white">{data ? data.name : 'Student 360 Profile'}</h3>
              <p className="text-sm font-bold text-slate-400">
                Class <span className="text-sky-400">{classId}</span> {data && data.rollNumber ? `| Roll ${data.rollNumber}` : ''} | <span className="text-indigo-400">{year}</span>
              </p>
            </div>
            <button type="button" onClick={onClose} 
              className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-slate-400 transition-colors hover:bg-slate-700 hover:text-white" aria-label="Close">
              <span className="text-xl">✕</span>
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <ErrorBox message={err} />
          {!data && !err && <Loading />}
          {data && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl bg-slate-950/30 p-2">
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={data.radar} outerRadius="75%">
                    <PolarGrid stroke="#334155" />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 600 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#475569', fontSize: 10 }} />
                    <Radar dataKey="value" stroke={C.cyan} fill={C.cyan} fillOpacity={0.4} />
                    <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                <Row k="Attendance Consistency" v={`${data.attendance.pct}%`} 
                  sub={`${data.attendance.present} present / ${data.attendance.workingDays} working days`}
                  bad={!data.attendance.meetsBoardCriteria} 
                  note={data.attendance.meetsBoardCriteria ? '✅ Meets 75% Board Criteria' : '⚠️ Below 75% Threshold'} 
                />
                <Row k="Homework Discipline" v={`${data.homework.disciplinePct}%`} 
                  sub={`${data.homework.pending} pending day(s)`} 
                />
                <Row k="Academic Performance" v={data.exams.percent == null ? 'N/A' : `${data.exams.percent}%`} 
                  sub={data.exams.classPercentile == null ? 'Awaiting marksheet' : `Class Percentile: ${data.exams.classPercentile}`} 
                />
                <Row k="Approved Leave" v={`${data.attendance.leave} day(s)`} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v, sub, note, bad }) => (
  <div className={`rounded-2xl border p-4 transition-all ${bad ? 'border-rose-500/30 bg-rose-500/5' : 'border-slate-800 bg-slate-800/40 hover:border-slate-700'}`}>
    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{k}</div>
    <div className="flex items-baseline gap-2">
      <div className={`text-xl font-black ${bad ? 'text-rose-400' : 'text-white'}`}>{v}</div>
      {sub && <div className="text-xs font-medium text-slate-400">{sub}</div>}
    </div>
    {note && (
      <div className={`mt-2 text-[10px] font-bold ${bad ? 'text-rose-400' : 'text-emerald-400'}`}>
        {note}
      </div>
    )}
  </div>
);
